import { useAtomValue } from "jotai";
import type { NextPage, PageProps } from "next";
import { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { notEmpty, parseQueryString } from "shared/lib/utils";

import { useTopBannerAdSlot } from "scmp-app/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import { PageSeo } from "scmp-app/components/meta";
import { SearchContextProvider } from "scmp-app/components/search/contexts";
import { sanitizeKeyword } from "scmp-app/components/search/helpers";
import { SearchExploreTopics } from "scmp-app/components/search/search-explore-topics";
import { SearchInput } from "scmp-app/components/search/search-input";
import { SearchResultPanel } from "scmp-app/components/search/search-result-panel";
import { trackingAtom } from "scmp-app/components/tracking";

import { SearchContainer } from "./styles";

type Props = PageProps & { q: string };

const SearchPage: NextPage<Props> = ({ q: routeQueryValue }) => {
  const { refresh: refreshAdSlot } = useTopBannerAdSlot({
    desktop: {
      adUnit: "d_banner2a",
      sizes: [
        [970, 250],
        [728, 90],
        [970, 90],
      ],
      zone: "search/home",
    },
    mobile: null,
  });

  const cleanRouteQueryValue = sanitizeKeyword(routeQueryValue);
  const [currentValue, setCurrentValue] = useState(cleanRouteQueryValue);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isShowTopics, setIsShowTopics] = useState(false);

  useEffect(() => {
    setCurrentValue(cleanRouteQueryValue);
    setSearchInputValue(cleanRouteQueryValue);
  }, [cleanRouteQueryValue]);

  useEffect(() => {
    setIsShowTopics(
      !(notEmpty(currentValue) || notEmpty(searchInputValue) || notEmpty(cleanRouteQueryValue)),
    );
  }, [currentValue, searchInputValue, cleanRouteQueryValue]);

  useEffect(() => {
    if (currentValue === cleanRouteQueryValue) return;
    refreshAdSlot();
  }, [currentValue, refreshAdSlot, cleanRouteQueryValue]);

  // The condition for tracking page view is different with refreshAdSlot
  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView, cleanRouteQueryValue]);

  return (
    <SearchContextProvider currentValue={currentValue} searchPageType="default">
      <PageSeo title="Search" />
      <SearchContainer>
        <SearchInput
          onChange={value => {
            setSearchInputValue(value ?? "");
          }}
        />
        <ErrorBoundary fallback={<></>}>{isShowTopics && <SearchExploreTopics />}</ErrorBoundary>
        <SearchResultPanel />
      </SearchContainer>
    </SearchContextProvider>
  );
};

SearchPage.getInitialProps = context => ({
  animateKey: "search",
  appBarConfiguration: {
    hasDesktopAd: true,
    hasMobileAd: false,
    variant: "scmp/slim",
  },
  q: parseQueryString(context.query.q) ?? "",
});

export default SearchPage;
